import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { bool, func, object, shape, string, number, array } from 'prop-types';
import {
  deleteConfigureView,
  moveConfigureView,
  getElementTree,
  getElementTreeReset,
  saveView,
  setAsDefault
} from '../MarketPricingActions';
import { showSlideout, hideSlideout } from 'ps-components/Slideout/SlideoutReducer';
import { ViewList } from '../../Authorization/ComponentsWithAuthorization';
import EditView from './EditView';
import { PAGE } from '../../../lib/pageCodeDefaults';

import './ConfigureViewContainer.scss';

ConfigureViewContainer.propTypes = {
  // from calling component
  onRefresh: func,
  categoryCode: string,
  overrideCategoryCode: string,
  page: string,
  pageCode: string,
  pageName: string,
  onRefreshData: func,
  includeAggregates: bool,
  views: array, /* the next three are optional and are used when global state isn't used */
  defaultViewId: number,
  selectedViewPk: number,
  useOverrideLabelFormatter: bool,
  elementsFilter: func,
  categoryFilter: func,
  elementCountLimit: number,
  hideRequiredFields: bool,
  showAggregates: bool,
  // from redux in this component
  maid: string,
  permissions: shape({
    regularUser: bool,
    readOnlyUser: bool,
    reportViewUser: bool,
    restrictedUser: bool,
    groupAdmin: bool,
    siteAdmin: bool,
    globalAdmin: bool,
    superAdmin: bool
  }),
  subsite: string,
  marketPricing: shape({
    deleteView: shape({
      loading: bool,
      error: string
    }),
    moveView: shape({
      loading: bool,
      error: string
    }),
    elementTree: shape({
      data: array,
      defaultFields: array,
      currentFields: array,
      loading: bool,
      loaded: bool,
      error: string
    }),
    saveView: shape({
      loading: bool,
      error: string
    }),
    setDefault: shape({
      loading: bool,
      error: string
    })
  }),
  slottedSearch: object,
  jobPricing: object,
  landingPage: object,
  landingDashboard: object,
  jobDetails: object,
  jobEmployees: object,
  reviewGeoDifferential: object,
  actions: shape({
    showSlideout: func,
    hideSlideout: func,
    onDelete: func,
    onMove: func,
    getElementTree: func,
    getElementTreeReset: func,
    saveView: func,
    setAsDefault: func
  }),
  groupId: number,
  organizationId: number,
  groupAdminCanConfigureViews: bool,
  regularUserCanConfigureViews: bool,
  innerComponent: object,
  onCreate: func,
  onDelete: func,
  onCopy: func,
  onClickView: func,
  navigateToPrevSlideout: func,
  isGroupingEnabled: bool
};

ConfigureViewContainer.defaultProps = {
  isGroupingEnabled: true
};

function ConfigureViewContainer(props) {
  const [showEditView, setShowEditView] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedView, setSelectedView] = useState();

  useEffect(() => {
    setShowEditView(false);
  }, [props.categoryCode]);

  function toggleEditView() {
    setShowEditView(!showEditView);
  }

  function getInnerComponent() {
    const {
      page,
      jobPricing,
      slottedSearch,
      marketPricing,
      landingPage,
      landingDashboard,
      jobDetailsData,
      jobEmployees,
      navigateToPrevSlideout,
      reviewGeoDifferential,
      isGroupingEnabled,
      elementsFilter,
      categoryFilter,
      elementCountLimit
    } = props;

    let views = props.views || [];
    let defaultViewId = props.defaultViewId;
    let currentViewId = props.selectedViewPk;
    let disableGroupInput;
    let showAggregates = props.showAggregates || false;

    // pricing details views
    if (page === PAGE.PRICING_DETAILS) {
      views = jobPricing.pricingViews;
      defaultViewId = jobPricing.pricingDefaultViewId;
      currentViewId = jobPricing.selectedViewPk;
    }

    // slotted details views
    if (page === PAGE.SLOTTED_DETAILS) {
      views = jobPricing.slottedViews;
      defaultViewId = jobPricing.slottedDefaultViewId;
      currentViewId = jobPricing.selectedViewSlottedPk;
    }

    // survey search views
    if (page === PAGE.SURVEY_SEARCH) {
      views = marketPricing.surveyViews?.views;
      defaultViewId = marketPricing.surveyViews?.defaultViewId;
      currentViewId = marketPricing.surveySelectedView?.selectedViewPk;
    }

    // slotted search views
    if (page === PAGE.SLOTTED_SEARCH) {
      views = slottedSearch.slottedSearchViews?.views;
      defaultViewId = slottedSearch.slottedSearchViews?.defaultViewId;
      currentViewId = slottedSearch.slottedSearchSelectedView?.selectedViewPk;
      showAggregates = true;
    }

    // landing page job views
    if (page === PAGE.JOB_SEARCH) {
      views = landingPage.landingPageViews?.views;
      defaultViewId = landingPage.landingPageViews?.defaultViewId;
      currentViewId = landingPage.landingPageSelectedView?.selectedViewPk;
      showAggregates = true;
    }

    if (page === PAGE.REVIEW_IMPACT) {
      views = reviewGeoDifferential.reviewImpactViews?.views;
      defaultViewId = reviewGeoDifferential.reviewImpactViews?.defaultViewId;
      currentViewId = reviewGeoDifferential.reviewImpactSelectedView?.selectedViewPk;

      disableGroupInput = element => {
        return element.field_type_code === 'monetary' || element.field_type_code === 'percent';
      };
    }
    if (page === PAGE.JOB_EMPLOYEES) {
      views = jobEmployees.employeeViews?.views;
      defaultViewId = jobEmployees.employeeViews?.defaultViewId;
      currentViewId = jobEmployees.employeeSelectedView?.selectedViewPk;

      disableGroupInput = () => {
        return true;
      };
    }
    if (page === PAGE.JOB_DETAILS) {
      views = jobDetailsData.jobDetailsViews?.views;
      defaultViewId = jobDetailsData.jobDetailsViews?.defaultViewId;
      currentViewId = jobDetailsData.jobDetailsSelectedView?.selectedViewPk;

      disableGroupInput = () => {
        return true;
      };
    }

    // Dashboard page views
    if (page === PAGE.DASHBOARD) {
      views = landingDashboard?.views;
      defaultViewId = landingDashboard?.defaultView?.view_pk;
      currentViewId = landingDashboard?.currentView?.view_pk;
    }

    if (showEditView) {
      return (
        <EditView
          {...props}
          views={views}
          defaultViewId={defaultViewId}
          toggleEditView={toggleEditView}
          editMode={editMode}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          currentViewId={currentViewId}
          disableGroupInput={disableGroupInput}
          navigateToPrevSlideout={navigateToPrevSlideout}
          isGroupingEnabled={isGroupingEnabled}
          showAggregates={showAggregates}
          elementsFilter={elementsFilter}
          categoryFilter={categoryFilter}
          elementCountLimit={elementCountLimit}
          hideAggs={props.pageCode === 'bulk_pricing_org_jobs'}
        />
      );
    }

    return (
      <ViewList
        {...props}
        views={views}
        defaultViewId={defaultViewId}
        currentViewId={currentViewId}
        toggleEditView={toggleEditView}
        setEditMode={setEditMode}
        setSelectedView={setSelectedView}
        navigateToPrevSlideout={navigateToPrevSlideout}
      />
    );
  }

  return (
    <div className="configure-view__container">
      {props.innerComponent}
      {getInnerComponent()}
    </div>
  );
}

const mapStateToProps = ({
  session,
  marketPricing,
  landingPage,
  landingDashboard,
  reviewGeoDifferential,
  jobEmployees,
  jobDetailsData
}) => {
  return {
    maid: session.organization.masterAccountId,
    permissions: session.user.rights,
    subsite: session.organization.code,
    marketPricing: marketPricing.MarketPricingReducer,
    slottedSearch: marketPricing.SlottedSearchReducer.slottedSearchReducer,
    jobPricing: marketPricing.JobPricingReducer.MainJobPricingReducer,
    landingPage: landingPage.landingPageReducer,
    landingDashboard: landingDashboard.MainDashboardReducer,
    groupId: Number(session.group.id),
    organizationId: Number(session.organization.defaultGroup.id),
    groupAdminCanConfigureViews: session.groupAdminCanConfigureViews,
    regularUserCanConfigureViews: session.regularUserCanConfigureViews,
    reviewGeoDifferential: reviewGeoDifferential.reviewImpactReducer,
    jobEmployees: jobEmployees.JobEmployeesMainReducer,
    jobDetailsData: jobDetailsData
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      showSlideout,
      hideSlideout,
      onDelete: deleteConfigureView,
      onMove: moveConfigureView,
      getElementTree,
      getElementTreeReset,
      saveView,
      setAsDefault
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(ConfigureViewContainer);
